import React, { useEffect, useState } from 'react';
import {
    Button,
    Input,
    Slider,
    Toast,
    Spinner,
    TextArea,
    Alerts
} from '@kaleyra/emmy';
import Styles from './ipConfiguration.module.scss';
import { useTranslation } from 'react-i18next';
import ErrorIcon from '../../../../assets/error.svg';
import { range } from 'lodash';
import { checkIfValidIPV6, checkWhiteListedApiKeyLimit, removeBackSlash, validateIpAddress } from '../../../../utils';

export type MapStatePropsType = {
    sidebarVisibility: boolean;
    params: any;
};

export type DispatchPropsType = {
    onClose: () => void;
    onEditIpConfiguration: (data: any, callBack: any) => void;
    onLoad: () => void;
};

const EditIpConfiguration: React.FC<MapStatePropsType & DispatchPropsType> = ({
    onClose,
    onEditIpConfiguration,
    onLoad,
    params,
    sidebarVisibility
}) => {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [configName, setConfigName] = useState('');
    const [configNameErr, setConfigNameErr] = useState('');
    const [ipConfigErr, setIpConfigErr] = useState('');
    const [ipConfig, setIpConfig] = useState('');
    const [edit, setEditing] = useState(false);
    const allowedIpRangeIpv4 = range(8, 31);
    const allowedIpRangeIpv6 = range(1, 129);
    const { original } = params;
    const dispIp = removeBackSlash(original?.ip_addresses);
    
    useEffect(() => {

        setConfigName(original?.ip_config_name);
        setIpConfig(dispIp);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnSave = () => {
        setEditing(true);
        onEditIpConfiguration(({
            ip_config_id: params?.original?.id,
            ip_config_name: original?.ip_config_name !== configName ? configName : undefined,
            ip_addresses: dispIp !== ipConfig ? ipConfig.split(',') : undefined,
            is_config_shared: '0'
        }), (res: any, success: boolean) => {
            if (success) {
                onClose();
                onLoad();
                setSaving(false);
                setEditing(false);
                Toast(t("KLR02711"), 'success');
            }
            else {
                setEditing(false);
                Toast(res?.data?.error?.IpConfigExists || res?.data?.message, 'error');
            }
        })
    };

    const getFooter = (): React.ReactNode => {
        return (
            <div className={Styles.footer}>
                <Button type="tertiary" onClick={onClose}>
                    {t("mainCancel")}
                </Button>
                <Button
                    className={Styles.leftMargin}
                    disabled={checkSaveButtonEnabled()}
                    onClick={() => {
                        (original?.associated_api_keys?.subaccount_api_keys?.count > 0 || original?.associated_api_keys?.parent_api_keys?.count > 0) ? setSaving(true) : handleOnSave()
                    }}
                >{edit ? <Spinner margin={"1rem"} /> :t("mainSave")}
                </Button>
            </div>
        );
    };

    const getConfirmationFooter = (): React.ReactNode => {
        return (
            <div className={Styles.footer}>
                <Button type="tertiary" onClick={() => setSaving(false)}>
                    {t("mainCancel")}
                </Button>
                <Button
                    className={Styles.leftMargin}
                    onClick={() => handleOnSave()}
                    disabled={edit}
                >{edit ? <Spinner margin={"1.5rem"} /> : t("KLR02683")}
                </Button>
            </div>
        );
    };

    const validateCheck = (field: string) => {

        let res = ipConfig.split(',');
        const duplicateIps = res.filter((item: any, index: number) => res.indexOf(item) !== index);

        if (field === 'configName') {
            if (configName.trim() === '') {
                return t('titleReq');
            }
            if (configName?.length < 3) {
                return t('min3Char');
            }
            if (configName?.length > 50) {
                return t('KLR02819');
            }
            return;
        }
        if (field === 'ipRange') {
            if (!String(res)) {
                return t('titleReq');
            }
            if (res.length > checkWhiteListedApiKeyLimit()) {
                return t("KLR02668", { "maxIpCount": checkWhiteListedApiKeyLimit() });
            }
            if (duplicateIps.length > 0) {
                return t("KLR02669");
            }
            for (let i = 0; i < res.length; i++) {
                let ip = res[i].split('/')
                if (res[i].includes('/')) {
                    if (!validateIpAddress((ip)[0])
                        || ip.includes('')
                        || (checkIfValidIPV6((ip)[0]) ? !allowedIpRangeIpv6.includes(Number(ip[1])) : !allowedIpRangeIpv4.includes(Number(ip[1])))
                    ) {
                        return t('apiValidIp');
                    }
                }
                else {
                    if (!validateIpAddress((ip)[0])) {
                        return t('apiValidIp');
                    }
                }
            }
            return;

        }
        return;
    };

    const handleConfigNameOnBlur = () => {
        let errMsg = validateCheck('configName')
        if (errMsg) {
            setConfigNameErr(errMsg);
            return;
        } else {
            setConfigNameErr('');
            return;
        };
    };

    const handleIpRangeOnBlur = () => {
        let errMsg = validateCheck('ipRange');
        if (errMsg) {
            setIpConfigErr(errMsg);
            return;
        } else {
            setIpConfigErr('');
            return;
        };
    };

    const validateFields = (field: string): 'error' | undefined => {
        if (validateCheck(field)) {
            return "error";
        }
        return undefined;
    };

    const setErrors = (field: string) => {
        let errMsg = validateCheck(field);
        if (errMsg) {
            return (
                <>
                    <img
                        src={ErrorIcon}
                        alt=""
                        className={Styles.errorIcon}
                    />
                    {errMsg}
                </>
            );
        };
    };

    const checkSaveButtonEnabled = () => {

        if (dispIp === ipConfig && original?.ip_config_name === configName) {
            return true;
        }
        if ((validateCheck('ipRange') || validateCheck('configName')) || edit)
            return true;
        return false;
    };

    const getContent = () => {
        return (
            <div className={Styles.sliderContainer}>
                <div className={Styles.bottomMargin}>
                    <Alerts
                        content={t("KLR02684")}
                        type='draft'
                    />
                </div>
                <Input
                    label={t("KLR02679")}
                    placeholder={t("KLR02679")}
                    value={configName}
                    bottomLabelType={configNameErr ? validateFields('configName') : undefined}
                    bottomLabel={configNameErr ? setErrors('configName') : null}
                    onChange={(e: any) => {
                        setConfigNameErr('');
                        setConfigName(e.target.value);
                    }}
                    onBlur={() => {
                        handleConfigNameOnBlur();
                    }}
                    showBottomLabelSpace={false}
                />
                <div className={Styles.ipAddress}>{t("KLR02680")}</div>
                <div className={Styles.ipAddressDesc}>{t("KLR02681")}</div>
                <TextArea
                    bordered
                    value={ipConfig}
                    label={t("KLR02680")}
                    placeholder={t('IPplaceholder')}
                    bottomLabelType={ipConfigErr ? validateFields('ipRange') : undefined}
                    bottomLabel={ipConfigErr ? setErrors('ipRange') : null}
                    onChange={(e: any) => {
                        setIpConfigErr('');
                        setIpConfig(e.target.value.replace(/\s/g, ''));
                    }}
                    onBlur={() => {
                        handleIpRangeOnBlur();
                    }}
                    showBottomLabelSpace={false}
                />
                <div className={Styles.ipAddressDescBottom}>{t('whitelistedTltip')}</div>
            </div>
        )
    };

    const getEditConfirmation = () => {
        return (
            <>
             {
                    original?.associated_api_keys?.subaccount_api_keys?.count > 0 &&
                    <div className={Styles.sliderContainer}>
                        <Alerts
                            type='warning'
                            content={t("KLR02764")}
                        />
                    </div>
                }
                <div className={Styles.editConfirmation}>{t("KLR02685")}</div>
                <ul className={Styles.list}>
                    <li className={Styles.next}>
                        {t("KLR02686")}
                    </li>
                    <li>
                        {t("KLR02687")}
                    </li>
                </ul>
            </>
        )
    }

    return (
        <Slider
            containerId="root-modal"
            onClose={() => saving ? setSaving(false) : onClose()}
            visibility={sidebarVisibility}
            footer={saving ? getConfirmationFooter() : getFooter()}
            title={saving ? t("KLR02688") : t("KLR02689")}
        >
            {saving ? getEditConfirmation() : getContent()}

        </Slider>
    );
};

export default EditIpConfiguration;
